import React, {
  FormEvent,
  cloneElement,
  useEffect,
  useState,
  RefObject,
} from 'react';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { FuelTextField } from '@frete.com/fuel-react';
import { RefCallBack } from 'react-hook-form';
import { SpacingType } from 'styles/styled';
import useDebounce from '@hooks/useDebounce';
import DropDown from '../DropDown';
import AutoCompleteList, { SuggestionInterface } from './List';

export interface AutoCompletePropsInterface {
  name: string;
  borderOff?: boolean;
  customDropdownSpacing?: keyof SpacingType;
  customMaxHeight?: number;
  blockListMode?: boolean;
  startOpen?: boolean;
  label: string;
  placeholder?: string;
  leftIcon?: React.ReactElement;
  emptyMessage: string;
  error?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onClearSelectedSuggestion?: () => void;
  onSelectSuggestion: (suggestion: SuggestionInterface) => void;
  onSuggestionsQuery(
    description: string,
    options?: UseQueryOptions<SuggestionInterface[], Error>,
  ): UseQueryResult<SuggestionInterface[], Error>;
  onSearchTyped?: (term: string) => void;
  validInputFormat?: RegExp;
  defaultValue?: string;
  rightInputIcon?: React.ReactElement;
  rightTagText?: {
    positive: string;
    negative: string;
  } | null;
  rightPrefixText?: string | null;
  canOpenWithoutTypedText?: boolean;
  keepOpen?: boolean;
  innerRef?: RefCallBack | RefObject<HTMLFuelTextFieldElement>;
  maxLength?: number;
}

function AutoComplete({
  name,
  label,
  placeholder,
  leftIcon,
  rightInputIcon,
  emptyMessage,
  error,
  onBlur,
  onFocus,
  onClearSelectedSuggestion,
  onSelectSuggestion,
  onSuggestionsQuery,
  customMaxHeight,
  borderOff,
  customDropdownSpacing,
  blockListMode = false,
  startOpen = false,
  onSearchTyped,
  validInputFormat,
  defaultValue,
  rightTagText = null,
  rightPrefixText = null,
  canOpenWithoutTypedText = false,
  keepOpen = false,
  innerRef,
  maxLength,
}: AutoCompletePropsInterface) {
  const [searchTerm, setSearchTerm] = useState(defaultValue || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedValue, setSelectedValue] = useState(defaultValue || '');
  const [isOpen, setOpen] = useState(startOpen || keepOpen);
  const { debounce } = useDebounce(300);
  const { data: suggestions } = onSuggestionsQuery(debouncedSearchTerm, {
    keepPreviousData: false,
  });

  const handleInput = (event: FormEvent<HTMLFuelTextFieldElement>) => {
    const { value } = event.target as HTMLInputElement;

    if (selectedValue !== value) {
      setSelectedValue('');
      setSearchTerm(value);

      if (onClearSelectedSuggestion) {
        onClearSelectedSuggestion();
      }
    }

    setOpen(true);

    setTimeout(() => {
      const parsedValue = validInputFormat
        ? value.replace(validInputFormat, '')
        : value;

      setSearchTerm(parsedValue);

      if (onSearchTyped) {
        onSearchTyped(parsedValue);
      }
    }, 10);
  };

  const handleFocus = () => {
    if (canOpenWithoutTypedText || searchTerm) setOpen(true);

    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }

    setOpen(false);
  };

  const handleSelectSuggestion = (suggestion: SuggestionInterface) => {
    setSelectedValue(suggestion.description);
    onSelectSuggestion(suggestion);
  };

  useEffect(() => {
    setSearchTerm(defaultValue || '');
    setSelectedValue(defaultValue || '');
  }, [defaultValue]);

  useEffect(() => {
    debounce(() => setDebouncedSearchTerm(searchTerm));
  }, [searchTerm]);

  const rightIcon =
    rightInputIcon && cloneElement(rightInputIcon, { slot: 'suffix' });

  return (
    <>
      <FuelTextField
        name={name}
        data-testid={`input-${name}`}
        type="text"
        label={label}
        placeholder={placeholder}
        onInput={handleInput}
        hasSuffix={Boolean(rightInputIcon)}
        state={error ? 'error' : 'default'}
        hint={error || ''}
        value={selectedValue || searchTerm || defaultValue}
        onBlur={handleBlur}
        onFocus={handleFocus}
        ref={innerRef}
        maxlength={maxLength}
      >
        {rightIcon}
      </FuelTextField>
      <DropDown
        open={keepOpen || (isOpen && Boolean(suggestions))}
        borderOff={borderOff}
        dropdownSpacing={customDropdownSpacing}
        customMaxHeight={customMaxHeight}
        data-testid={`dropDown-${name}`}
      >
        <AutoCompleteList
          rightTagText={rightTagText}
          name={`field-item-${name}`}
          emptyMessage={emptyMessage}
          blockListMode={blockListMode}
          leftIcon={leftIcon}
          searchTerm={searchTerm}
          rightPrefixText={rightPrefixText}
          suggestionsList={suggestions || []}
          onSelectSuggestion={handleSelectSuggestion}
        />
      </DropDown>
    </>
  );
}

export default AutoComplete;
